// HTMLAudioElements for fallback playback (max volume 1.0). The real
// playback path is Web Audio with a GainNode so we can boost above 1.0.
const sounds = {
    chime: new Audio('assets/sounds/chime.mp3'),
    swoosh: new Audio('assets/sounds/swoosh.mp3'),
    explosion: new Audio('assets/sounds/explosion.mp3'),
    scroll_over: new Audio('assets/sounds/scroll_over.mp3'),
    click: new Audio('assets/sounds/click.mp3'),
    shoot: new Audio('assets/sounds/shoot.mp3'),
    kill: new Audio('assets/sounds/kill.mp3'),
    hit: new Audio('assets/sounds/hit.mp3'),
    critical_hit: new Audio('assets/sounds/critical_hit.mp3')
};

// Per-sound gain multiplier (1.0 = unchanged). UI sounds are boosted
// well past unity gain since the source files are quiet.
const SOUND_GAIN = {
    scroll_over: 9.0,
    click: 9.0,
    chime: 1.5,
    swoosh: 1.5,
    explosion: 2.0,
    shoot: 1.2,
    kill: 1.5,
    hit: 1.2,
    critical_hit: 1.6
};

// Cached AudioBuffers per sound name, populated by detectStartOffsets.
// When present, playSound uses the Web Audio path (loud + per-trigger
// voice). When missing, falls back to HTMLAudioElement.
const audioBuffers = {};
let sharedAudioCtx = null;
function getAudioContext() {
    if (sharedAudioCtx) return sharedAudioCtx;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    try { sharedAudioCtx = new Ctx(); } catch (_) { return null; }
    return sharedAudioCtx;
}

const SILENCE_THRESHOLD = 0.01; // signal must exceed this normalized amplitude
const SILENCE_PAD_SECONDS = -0.2; // skip 0.2s INTO the real audio past the first activity

async function detectStartOffsets() {
    const ctx = getAudioContext();
    if (!ctx) return;
    for (const [name, audio] of Object.entries(sounds)) {
        if (audioBuffers[name]) continue; // already decoded
        try {
            const resp = await fetch(audio.src, { cache: 'force-cache' });
            if (!resp.ok) continue;
            const buf = await resp.arrayBuffer();
            const decoded = await ctx.decodeAudioData(buf);
            audioBuffers[name] = decoded;
            const channel = decoded.getChannelData(0);
            let firstActive = 0;
            for (let i = 0; i < channel.length; i++) {
                if (Math.abs(channel[i]) > SILENCE_THRESHOLD) {
                    firstActive = i;
                    break;
                }
            }
            const rawSilenceEnd = firstActive / decoded.sampleRate;
            const offset = Math.min(
                Math.max(0, rawSilenceEnd - SILENCE_PAD_SECONDS),
                Math.max(0, decoded.duration - 0.05)
            );
            audio._startOffset = offset;
            console.log(`[sounds] ${name}: silence ends at ${(rawSilenceEnd * 1000).toFixed(0)}ms, playing from ${(offset * 1000).toFixed(0)}ms (gain ${SOUND_GAIN[name] || 1})`);
        } catch (e) {
            // decodeAudioData can fail before a user gesture in some
            // browsers — leave _startOffset undefined and we'll fall
            // back to HTMLAudioElement playback at the next trigger.
        }
    }
}

detectStartOffsets();
let _pendingRetry = true;
function retryDetectOnGesture() {
    if (!_pendingRetry) return;
    _pendingRetry = false;
    document.removeEventListener('pointerdown', retryDetectOnGesture, true);
    document.removeEventListener('keydown', retryDetectOnGesture, true);
    detectStartOffsets();
    // Also resume a suspended context (Safari/Chrome autoplay policy).
    if (sharedAudioCtx && sharedAudioCtx.state === 'suspended') {
        try { sharedAudioCtx.resume(); } catch (_) {}
    }
}
if (typeof document !== 'undefined') {
    document.addEventListener('pointerdown', retryDetectOnGesture, true);
    document.addEventListener('keydown', retryDetectOnGesture, true);
}

const scenes = {
    Home: { image: 'assets/images/snordeluxe-background.webp', music: 'assets/music/lobby-music.mp3' },
    Battleground: { image: 'assets/images/snordelux-battlefield.webp', music: 'assets/music/battle-music.mp3' },
};

function preloadImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = url;
        img.onload = resolve;
        img.onerror = reject;
    });
}

function preloadAudio(url) {
    return new Promise((resolve, reject) => {
        const audio = new Audio();
        audio.src = url;
        audio.preload = 'auto';
        audio.oncanplaythrough = resolve;
        audio.onerror = reject;
    });
}

function preloadSounds() {
    return Promise.all(Object.values(sounds).map(sound => {
        return new Promise((resolve, reject) => {
            sound.oncanplaythrough = resolve;
            sound.onerror = reject;
        });
    }));
}

async function preloadAssets() {
    const imagePromises = [];
    const audioPromises = [];

    for (const scene of Object.values(scenes)) {
        if (scene.image) {
            imagePromises.push(preloadImage(scene.image));
        }
        if (scene.music) {
            audioPromises.push(preloadAudio(scene.music));
        }
    }

    await Promise.all([...imagePromises, ...audioPromises, preloadSounds()]);
    console.log('All assets preloaded successfully.');
}

const PLAY_DEBOUNCE_MS = 60;
const _lastTriggerAt = {};

function playSound(effectName) {
    const sound = sounds[effectName];
    if (!sound) return;

    const now = performance.now();
    if (now - (_lastTriggerAt[effectName] || 0) < PLAY_DEBOUNCE_MS) return;
    _lastTriggerAt[effectName] = now;

    const ctx = getAudioContext();
    const buffer = audioBuffers[effectName];
    const offset = sound._startOffset || 0;
    const gain = SOUND_GAIN[effectName] != null ? SOUND_GAIN[effectName] : 1.0;

    // Preferred path: Web Audio with GainNode for above-unity volume +
    // independent per-trigger voices (no cancel, no max-volume cap).
    if (ctx && buffer) {
        if (ctx.state === 'suspended') { try { ctx.resume(); } catch (_) {} }
        try {
            const src = ctx.createBufferSource();
            src.buffer = buffer;
            const g = ctx.createGain();
            g.gain.value = gain;
            src.connect(g).connect(ctx.destination);
            src.start(0, offset);
            return;
        } catch (e) {
            // Fall through to the HTMLAudioElement path on any failure.
        }
    }

    // Fallback path: cloneNode the HTMLAudioElement (capped at vol 1.0).
    const clone = sound.cloneNode();
    clone.volume = 1.0;
    try { clone.currentTime = offset; } catch (_) {}
    clone.addEventListener('ended', () => { try { clone.src = ''; } catch (_) {} });
    const p = clone.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
}

export { sounds, scenes, preloadAssets, playSound };
